import { BookingCalendarMonth } from '@components/Calendar/BookingCalendarMonth';
import { DayOverviewDrawer } from '@components/Calendar/DayOverviewDrawer';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { Trans } from '@lingui/react/macro';
import { BookingStatus } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { MonthCalendarDayInfo, NoddiLink } from 'noddi-ui-web';
import { customerRoutes, getEndOfMonth, getStartOfMonth, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useMemo, useState } from 'react';
import { FleetCalendarBooking, getCalendarStatusKey, getDateKey } from '../../../utils/fleetCalendarBookingDisplay';

const VISIBLE_STATUSES: BookingStatus[] = [
  BookingStatus.CONFIRMED,
  BookingStatus.ASSIGNED_TO_ROUTE,
  BookingStatus.COMPLETED
];
const PAGE_SIZE = 200;

export const BookingCalendarCard = () => {
  const { currentUserGroupId } = useAuthContext();
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [drawerDate, setDrawerDate] = useState<string | null>(null);

  const fromDate = noddiFormatDate(getStartOfMonth(currentDate), NoddiDateFormats.API_DATE);
  const toDate = noddiFormatDate(getEndOfMonth(currentDate), NoddiDateFormats.API_DATE);

  const { data, isFetching, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookings,
    input: {
      userGroupIds: [currentUserGroupId as number],
      statusIds: VISIBLE_STATUSES,
      deliveryWindowStartsAtGte: fromDate,
      deliveryWindowStartsAtLte: toDate,
      pageSize: PAGE_SIZE,
      pageIndex: 0
    },
    // Same as the full calendar: the previous month stays on screen while the next loads.
    queryConfig: { placeholderData: keepPreviousData }
  });

  const bookingsByDate = useMemo(() => {
    const filtered: FleetCalendarBooking[] = [];
    for (const booking of data?.results ?? []) {
      if (getCalendarStatusKey(booking.status.value) === null) {
        continue;
      }
      filtered.push(booking);
    }

    filtered.sort(
      (a, b) => new Date(a.deliveryWindowStartsAt).getTime() - new Date(b.deliveryWindowStartsAt).getTime()
    );

    const grouped = new Map<string, FleetCalendarBooking[]>();
    for (const booking of filtered) {
      const key = getDateKey(booking.deliveryWindowStartsAt);
      const list = grouped.get(key) ?? [];
      list.push(booking);
      grouped.set(key, list);
    }
    return grouped;
  }, [data]);

  if (error) {
    return <ApiErrorPageWithTrans error={error} />;
  }

  const handleDayClick = (info: MonthCalendarDayInfo) => setDrawerDate(info.dateString);

  return (
    <div className='flex flex-col gap-2 md:gap-4'>
      <h2 className='text-xl font-bold text-primary md:text-3xl'>
        <Trans>Booking calendar</Trans>
      </h2>

      <BookingCalendarMonth
        currentDate={currentDate}
        onMonthChange={setCurrentDate}
        onDayClick={handleDayClick}
        bookingsByDate={bookingsByDate}
        cellSize='compact'
        loading={isFetching}
        headerActions={
          <NoddiLink to={customerRoutes.fleetCalendar.getPath()} variant='link' size='small' endIcon='ArrowRight'>
            <Trans>Open full calendar</Trans>
          </NoddiLink>
        }
      />

      <DayOverviewDrawer
        dateString={drawerDate}
        bookings={drawerDate ? (bookingsByDate.get(drawerDate) ?? []) : []}
        onClose={() => setDrawerDate(null)}
        footer={
          <NoddiLink
            to={`${customerRoutes.fleetCalendar.getPath()}?from-date=${drawerDate}`}
            variant='primary'
            endIcon='ArrowRight'
            className='w-full'
          >
            <Trans>Open full calendar</Trans>
          </NoddiLink>
        }
      />
    </div>
  );
};
